import React from 'react';
import { cn } from '../../lib/utils';
import { useI18n } from '../../contexts/I18nContext';

// Resolve the traffic-light value from a container. `semaforo_aduanal`
// (verde/rojo) is the authoritative customs result; `release_traffic_light`
// (GREEN/RED/NA) is the legacy field. Some records only have the authoritative
// one populated, so derive from it first and fall back to the legacy field.
export function resolveTrafficLight(container) {
  const sem = (container?.semaforo_aduanal || '').toString().toLowerCase();
  if (sem === 'verde') return 'GREEN';
  if (sem === 'rojo') return 'RED';
  return container?.release_traffic_light || 'NA';
}

const statusColors = {
  // Shipment statuses
  'BOOKING_NO_SALIDO': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'NO_SALIDO_CHINA': 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
  'EN_TRANSITO_PUERTO': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'EN_TRANSITO_MARITIMO': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'EN_TRANSITO_TERRESTRE': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'ENTREGADO': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'ENTREGADO_PEND_DEMORAS': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',

  // Container statuses
  'NO_ARRIBADO': 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
  'ARRIBADO_PUERTO': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'EN_PUERTO': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'EN_PUERTO_LIBERACION': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'EN_PUERTO_LIBERADO': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'EN_PUERTO_SEMAFORO_ROJO': 'bg-red-500/20 text-red-400 border-red-500/30',
  'EN_PUERTO_CARGA_FERRO': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'DESPACHO_EN_PROCESO': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'LIBERADO': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'EN_TRANSITO': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'LLEGADA_PATIO': 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  'EN_PANTACO': 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  'EN_DESTINO': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',

  // Default
  'default': 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
};

export function StatusBadge({ code, label, status, className }) {
  const { t } = useI18n();
  // Support legacy `status` prop name
  const effectiveCode = code || status;
  const colorClass = statusColors[effectiveCode] || statusColors['default'];
  // Try i18n first, fallback to label, fallback to code
  const i18nKey = effectiveCode ? `statusCodes.${effectiveCode}` : null;
  const i18nLabel = i18nKey ? t(i18nKey) : null;
  const display = (i18nLabel && i18nLabel !== i18nKey) ? i18nLabel : (label || effectiveCode || '—');

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-sm',
        colorClass,
        className
      )}
    >
      {display}
    </span>
  );
}

export function TrafficLight({ status, days, size = 'md' }) {
  const { t } = useI18n();
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const statusClasses = {
    GREEN: 'traffic-light-green',
    RED: 'traffic-light-red',
    NA: 'traffic-light-off',
  };

  // Days mode: show numeric value with color
  if (days !== undefined && days !== null) {
    const colorClass = days > 7 ? 'text-red-400' : days > 3 ? 'text-amber-400' : 'text-emerald-400';
    return <span className={`text-xs font-mono ${colorClass}`}>{days}d</span>;
  }

  const labelKey = `trafficLight.${status || 'NA'}`;
  const titleKey = `trafficLight.${status || 'NA'}_TitleHelp`;
  const labelTxt = t(labelKey);
  const titleTxt = t(titleKey);

  return (
    <div className="flex items-center gap-1">
      <span
        className={cn(
          'rounded-full',
          sizeClasses[size],
          statusClasses[status] || statusClasses['NA']
        )}
        title={titleTxt !== titleKey ? titleTxt : labelTxt}
      />
      <span className="text-xs text-muted-foreground">
        {labelTxt !== labelKey ? labelTxt : 'N/A'}
      </span>
    </div>
  );
}

export function CostBadge({ calculated, cost }) {
  const { t } = useI18n();
  if (!calculated) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-sm">
        {t('costStatus.pending')}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-sm font-mono">
      {cost ? `$${cost.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : t('costStatus.calculated')}
    </span>
  );
}
